"use client";

/**
 * The reading surface for "where did the 3 GB go".
 *
 * `src/lib/gtaKnowledge/streamBudget.ts` does the counting and already refuses
 * to round a verdict away, to fold an unattributed file into a neighbour, or to
 * call a set of files clean that nobody compared. What is left here is the part
 * only a rendered page can get wrong, and this file is organised around the four
 * ways a surface silently undoes a correct library.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. THE NAME, WHICH IS THE FIRST DECISION
 *
 * The tool this replaces is called a load checker and measures no load. Nothing
 * in it correlates with tick time, resmon milliseconds, thread count or frame
 * rate: a 900 MB map pack of collision meshes costs a server essentially
 * nothing, and a 40 KB Lua file with a busy loop kills it. So this page says
 * what it measures — bytes a joining client downloads — in the first sentence
 * under its own heading, and names the tool that does measure CPU cost instead
 * of letting a reader assume this one does. A budget is a thing you can be over;
 * a "load score" is a number with no unit and no remedy.
 *
 * 2. THE SECOND PASS HAS TO BE VISIBLE, AND HAS TO BE ABLE TO FAIL
 *
 * Duplicate detection and the identical high-detail check need content, so the
 * page reads `SIGNATURE_PREFIX_BYTES` from each file whose size is shared with
 * another — `duplicateCandidatePaths` is what makes that a handful of short
 * reads rather than a second full scan. Three things about that pass are load
 * bearing:
 *
 *   - It is switchable, and switching it off does not produce "no duplicates".
 *     `analyzeBudget` with no signatures writes the sentence that says the
 *     comparison did not happen, and this page renders it. The drop is kept so
 *     the pass can be run afterwards on the same folder: a switch that costs a
 *     second trip through a directory dialog to change is one nobody changes,
 *     and then the default is the only behaviour the tool really has.
 *   - It reports its own arithmetic — candidates found, files read — because
 *     "we compared 6 of 412 files" and "we found no duplicates" are different
 *     claims and only one of them is true here.
 *   - **It refuses to sign a file whose bytes this tab does not hold.** That is
 *     not hypothetical: `intake.ts` takes a zip entry's size from the central
 *     directory *without inflating it*, which is the engineering that makes a
 *     39 MB archive scan instantly, and every binary entry therefore arrives as
 *     a `File` with a real `size` and an empty body. Hashing those would give
 *     every same-sized `.ytd` in the archive the identical signature and the
 *     page would confidently report six duplicates that are six different
 *     files. So a short read is detected by length and counted as unavailable,
 *     in words, and the whole zip path simply reports that it could not compare
 *     rather than reporting a fiction. See `readSignatures`.
 *   - **And it counts the two ways a file can be uncomparable separately,
 *     because they have different remedies.** A zip entry has a handle and no
 *     bytes; a file under no resource root has no handle at all, because
 *     `drop.resources[].files` is intake's analysable subset and a `backups/`
 *     folder beside `resources/` is in neither. Folding the second into the
 *     first printed a sentence about a zip's central directory over a folder
 *     pick containing no zip, telling the reader to pick the unpacked folder
 *     they had already picked. `handlesOfPick` recovers those files from the
 *     gesture's own `FileList` where the gesture has one, and the sentence for
 *     what is left names its real cause.
 *
 * 3. EVERY COUNT COUNTS EVERY ROW
 *
 * Thousands of files across hundreds of resources is the ordinary case, so every
 * list here draws a slice and reveals further slices on request — the same rule
 * `ResmonReport.tsx` and `SpawnIndexClient.tsx` keep, and for the same reason: a
 * "show all" button is the same freeze with a click in front of it. What makes a
 * cap honest is that it changes how many rows are on screen and nothing else.
 * Every figure, every bucket total and all three exports are computed off the
 * whole report.
 *
 * The stacked bar is drawn at true proportion with no minimum segment width. A
 * 0.2% bucket is a bucket you cannot see, and widening it to make it visible
 * would be the interface disagreeing with its own arithmetic; the legend under
 * the bar carries every bucket's exact byte count, including the invisible ones.
 *
 * 4. CAPS, PROGRESS AND A WAY OUT
 *
 * Theirs enforces no upload limit, no file count, no expanded size, no progress
 * and no cancel, and will happily try to buffer a 4 GB archive. `intake.ts`
 * already has the caps and the `AbortSignal`; what this page adds is the part a
 * library cannot do — refusing an obviously oversized pick *before* the read
 * with a sentence naming the number, a progress line, and a Cancel button that
 * abandons the scan rather than the tab. A refusal is its own phase, because a
 * refused drop rendered as a report of 0 bytes is the failure mode that made the
 * refusal worth having.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS SAVED, AND WHAT A DIFF CAN THEREFORE SEE
 *
 * The before-and-after workflow is the one their own documentation prescribes
 * and their tool makes impossible, so two scans are kept in `localStorage` in
 * two named slots — the shape `ResmonReport.tsx` already uses, read on the
 * client only and wrapped in try/catch at every access, because a private window
 * throws on the getter and a half-written value throws in `JSON.parse`.
 *
 * What is saved is a *snapshot*, not the report: totals, buckets, one row per
 * resource and one per extension. A `BudgetReport` carries a row per file and a
 * real `resources/` folder is tens of thousands of them, which is several
 * megabytes of JSON in a store that holds a few for the whole origin. The cost
 * of that decision is stated on the page rather than left to be discovered: a
 * diff can tell you a resource grew by 40 MB and cannot tell you which file
 * inside it did, and a resource whose total did not move is reported as
 * unchanged even if every file in it was replaced.
 *
 * `diffSnapshots` lives here rather than in `streamBudget.ts` for the same
 * reason: it diffs this page's saved shape, which is a presentation artifact. A
 * `diffBudget(before, after)` over two whole `BudgetReport`s belongs beside
 * `analyzeBudget`, and the module header there already names that seam.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS
 *
 *  - Bytes on disk, never VRAM. `src/lib/yftYtd.ts` can read the RSC7 page
 *    counts FiveM's own asset budget is expressed in and nothing in this page's
 *    import graph touches it, so no figure here is presented as memory pressure.
 *  - One scan at a time. Picking again replaces it, because `intake.ts` can only
 *    promise unique roots *within one drop* and unioning two picks is how two
 *    packs called `police` become one.
 *  - Nothing is uploaded. There is no `fetch`, `XMLHttpRequest`, `sendBeacon` or
 *    `WebSocket` in this page's import graph, and the sentence on screen is the
 *    claim a reader is invited to check against the code.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ChevronRight,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Save,
  Upload,
  X,
} from "lucide-react";
import { ShowTourButton } from "@/components/freeTools/ShowTourButton";
import { FindingsList } from "@/components/gta/FindingsList";
import { ToolWalkthrough } from "@/components/walkthrough/ToolWalkthrough";
import {
  steps as streamBudgetSteps,
  finishLabel as streamBudgetFinishLabel,
} from "@/components/walkthrough/steps/streamBudget";
import { downloadTextFile } from "@/lib/dom";
import {
  DEMO_INPUT_NOTE,
  DEMO_STATE_LABEL,
  demoDownloadName,
  demoPacksFor,
  type DemoPack,
  type DemoPackUse,
} from "@/lib/gtaKnowledge/demoPacks";
import {
  INTAKE_LIMITS,
  fromArchives,
  fromDataTransfer,
  fromDirectoryPick,
  type DataTransferLike,
  type ResourceDrop,
} from "@/lib/gtaKnowledge/intake";
import { pathKey } from "@/lib/gtaKnowledge/normalize";
import {
  BUDGET_BUCKETS,
  BUDGET_BUCKET_LABELS,
  SIGNATURE_PREFIX_BYTES,
  analyzeBudget,
  budgetSignature,
  budgetToCsv,
  budgetToMarkdown,
  duplicateCandidatePaths,
  formatBudgetBytes,
  type BudgetBucket,
  type BudgetReport,
  type BudgetResourceRow,
  type BudgetTally,
} from "@/lib/gtaKnowledge/streamBudget";

// ─── Caps ────────────────────────────────────────────────────────────────────
//
// Exported so the test asserts against the numbers the page uses rather than
// against a copy of them, which is how a cap gets raised in one place and tested
// in another.

/** Resource rows drawn before the tail is held back. */
export const BUDGET_RESOURCES_VISIBLE = 25;

/** How many more resource rows one click mounts. Never "all of them". */
export const BUDGET_RESOURCES_PAGE = 25;

/** File rows drawn inside one expanded resource before its own reveal appears. */
export const BUDGET_ASSETS_VISIBLE = 20;

/** How many more file rows one click mounts inside a resource. */
export const BUDGET_ASSETS_PAGE = 100;

/** Extension rows drawn before that table's reveal appears. */
export const BUDGET_EXTENSIONS_VISIBLE = 15;

/** How many more extension rows one click mounts. */
export const BUDGET_EXTENSIONS_PAGE = 25;

/**
 * The most files the second pass will read.
 *
 * Four kilobytes each is cheap and four thousand of them is not free, and the
 * candidate set on a server folder full of identically-sized placeholder assets
 * can be far larger than the duplicate set it is looking for. Whatever this cuts
 * is said out loud and folded into the report's own skipped list, so a scan that
 * hit the cap never reads as "no duplicates".
 */
export const MAX_SIGNED_FILES = 4_000;

/** Candidates read between yields, so progress paints and Cancel stays live. */
const SIGNATURE_BATCH = 32;

/**
 * The most files one pick may contain before the page refuses it outright.
 *
 * Deliberately equal to `INTAKE_LIMITS.maxEntries`, which is intake's own
 * per-drop ceiling for archive entries: one number, so a user who is told
 * 50,000 by one half of the tool is not told 200,000 by the other. A folder pick
 * has no such ceiling inside intake — nothing is buffered, so nothing throws —
 * which is exactly why the refusal has to be here, with a sentence naming the
 * count, rather than as a scan that takes four minutes and reports a number
 * nobody waited for.
 */
export const MAX_PICKED_FILES = INTAKE_LIMITS.maxEntries;

// ─── Small helpers ───────────────────────────────────────────────────────────

function n(value: number): string {
  return value.toLocaleString("en-US");
}

function count(value: number, singular: string, many?: string): string {
  return `${n(value)} ${value === 1 ? singular : (many ?? `${singular}s`)}`;
}

/** A whole per cent, which is as precise as a share of a download ever needs. */
function share(part: number, whole: number): string {
  if (whole <= 0) return "0%";
  return `${Math.round((part / whole) * 100)}%`;
}

// ─── Refusing a pick before reading it ───────────────────────────────────────

/**
 * The sentence that refuses an oversized pick, or `null` to go ahead.
 *
 * Before the read, not during it, and specific about the number: "that folder is
 * too big" is not a message anybody can act on. `formatBudgetBytes` is the
 * layer's own formatter, so the figure in a refusal and the figure in a table
 * are the same string built the same way.
 *
 * The archive branch checks the same ceiling `readArchive` checks before it
 * calls `arrayBuffer()`. Duplicated deliberately and read from `INTAKE_LIMITS`
 * rather than retyped: intake's refusal is a sentence in `truncated` on a drop
 * that then contains nothing, and a page that rendered that as a 0-byte report
 * would have turned a refusal into a clean result.
 */
export function refusePick(files: readonly File[], kind: "folder" | "archive"): string | null {
  if (files.length > MAX_PICKED_FILES) {
    return (
      `That pick holds ${count(files.length, "file")}, over the ${n(MAX_PICKED_FILES)}-file ` +
      "limit for one scan, so nothing was read. Scan one resources/ folder at a time rather " +
      "than a whole server backup."
    );
  }
  if (kind === "archive") {
    for (const file of files) {
      if (file.size > INTAKE_LIMITS.maxArchiveBytes) {
        return (
          `${file.name} is ${formatBudgetBytes(file.size)}, over the ` +
          `${formatBudgetBytes(INTAKE_LIMITS.maxArchiveBytes)} limit for a single archive, so ` +
          "nothing was read. Unpack it and pick the folder instead — a folder pick reads sizes " +
          "without loading any file into memory."
        );
      }
    }
  }
  return null;
}

// ─── The second pass ─────────────────────────────────────────────────────────

/** What one signature pass read, and what it could not. */
export type SignaturePass = {
  /** Keyed by drop-relative path, as `analyzeBudget` wants them. */
  signatures: Map<string, string>;
  /** Files whose size is shared with another, so worth reading at all. */
  candidates: number;
  /** Candidates actually signed. */
  read: number;
  /**
   * Candidates this page has a `File` for whose bytes are not in it.
   *
   * Almost always a binary entry from a zip: intake takes its size from the
   * central directory and never inflates it, so the `File` has a real size and
   * an empty body. Signing one would hash zero bytes and make every same-sized
   * file in the archive look identical.
   */
  unavailable: number;
  /**
   * Candidates this page has no `File` for at all.
   *
   * A different cause needing a different sentence, which is why it is a
   * different counter: these are files under no resource root that no gesture
   * handed a handle for — a loose entry inside an archive, or a file dragged in
   * outside any pack. Telling that reader about a zip's central directory and
   * asking them to pick the unpacked folder they have already picked is a
   * remedy that cannot be followed, which is worse than no sentence at all.
   */
  unheld: number;
  /** Candidates past `MAX_SIGNED_FILES`. */
  capped: number;
  /** The above, in sentences, for the report's skipped list. */
  notes: string[];
};

/** Nothing read, because the reader chose not to run the pass. */
function noPass(candidates: number): SignaturePass {
  return {
    signatures: new Map(),
    candidates,
    read: 0,
    unavailable: 0,
    unheld: 0,
    capped: 0,
    notes: [],
  };
}

/**
 * Drop-relative path → the `File` intake is holding for it, where it holds one.
 *
 * `drop.resources[].files` is intake's *analysable* subset, so it holds nothing
 * for a file under no resource root — the library header's own example of a
 * 200 MB `backups/` folder beside `resources/`. Those files are in `allPaths`,
 * are counted in every total, and were until recently named as duplicate
 * candidates and then never compared, under a sentence that blamed a zip's
 * central directory on a folder pick with no zip in it. `handlesOfPick` is the
 * other half: the gesture's own `FileList`, which holds every file the user
 * picked whether intake could attribute it or not.
 */
function filesByPath(drop: ResourceDrop): Map<string, File> {
  const map = new Map<string, File>();
  for (const resource of drop.resources) {
    for (const entry of resource.files) {
      map.set(pathKey(`${resource.root}/${entry.path}`), entry.file);
    }
  }
  return map;
}

/**
 * Every `File` a pick or a drop handed over, keyed the way `allPaths` is keyed.
 *
 * `webkitRelativePath` is exactly what `intake.fromDirectoryPick` builds its
 * paths from, so these keys and the drop's agree by construction rather than by
 * coincidence; a file picked on its own has an empty one and is keyed by name,
 * which is what intake does with it too.
 *
 * Empty for an archive pick — the entries are inside the zip and intake is the
 * only thing that ever held them — and near-empty for a dragged folder, whose
 * `DataTransfer.files` carries the top-level items and not their contents. Both
 * of those are honest gaps and `readSignatures` reports them as such.
 */
export function handlesOfPick(files: readonly File[]): Map<string, File> {
  const map = new Map<string, File>();
  for (const file of files) {
    const relative = "webkitRelativePath" in file ? file.webkitRelativePath : "";
    const path = typeof relative === "string" && relative !== "" ? relative : file.name;
    if (path === "") continue;
    map.set(pathKey(path), file);
  }
  return map;
}

/** Drop-relative path → the size intake recorded, which is what a signature keys on. */
function sizesByPath(drop: ResourceDrop): Map<string, number> {
  const map = new Map<string, number>();
  for (const entry of drop.allPaths) map.set(pathKey(entry.path), entry.size);
  return map;
}

/**
 * Read the first few kilobytes of every file worth comparing.
 *
 * `duplicateCandidatePaths` has already cut the set to files whose size is
 * shared with another, because two files of different lengths cannot be the same
 * file — on a real server folder that is an order of magnitude fewer reads, and
 * it is what makes this one short pass rather than a second scan of the pack.
 *
 * The length check after each read is the honesty rule doing its job. A `File`
 * whose body intake never inflated returns zero bytes for a 12 MB asset, and
 * `budgetSignature(size, emptyPrefix)` is a perfectly stable hash of nothing:
 * every same-sized file in the archive would collide, and the page would name
 * six vendor packs in a duplicate finding it invented. So a read shorter than
 * the prefix that file should have is not signed, and is counted.
 */
export async function readSignatures(
  drop: ResourceDrop,
  options?: {
    signal?: AbortSignal;
    onProgress?: (done: number, total: number) => void;
    /**
     * The gesture's own handles, for the files intake could not attribute to a
     * resource. Layered *under* intake's, which are the authority for anything
     * inside a resource — an archive's inflated entry is intake's `File` and
     * not the zip the user picked.
     */
    held?: ReadonlyMap<string, File>;
  },
): Promise<SignaturePass> {
  const candidates = duplicateCandidatePaths(drop);
  const pass = noPass(candidates.length);
  if (candidates.length === 0) return pass;

  const held = new Map<string, File>(options?.held ?? []);
  for (const [key, file] of filesByPath(drop)) held.set(key, file);
  const sizes = sizesByPath(drop);
  const wanted = candidates.slice(0, MAX_SIGNED_FILES);
  pass.capped = candidates.length - wanted.length;

  for (let index = 0; index < wanted.length; index += 1) {
    if (options?.signal?.aborted) {
      throw new DOMException("The scan was cancelled.", "AbortError");
    }
    const path = wanted[index];
    const key = pathKey(path);
    const file = held.get(key);
    const size = sizes.get(key) ?? 0;
    const expected = Math.min(size, SIGNATURE_PREFIX_BYTES);

    if (!file) {
      pass.unheld += 1;
    } else if (expected <= 0) {
      pass.unavailable += 1;
    } else {
      try {
        const prefix = new Uint8Array(await file.slice(0, SIGNATURE_PREFIX_BYTES).arrayBuffer());
        if (prefix.length < expected) {
          // The bytes are not there. See the argument above.
          pass.unavailable += 1;
        } else {
          pass.signatures.set(path, budgetSignature(size, prefix));
          pass.read += 1;
        }
      } catch {
        // A file moved or a permission revoked between the pick and the read.
        // One unreadable file is not a reason to abandon four hundred others.
        pass.unavailable += 1;
      }
    }

    if (index % SIGNATURE_BATCH === SIGNATURE_BATCH - 1) {
      options?.onProgress?.(index + 1, wanted.length);
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
  options?.onProgress?.(wanted.length, wanted.length);

  if (pass.unavailable > 0) {
    pass.notes.push(
      `${count(pass.unavailable, "file")} could not be compared because this tab does not hold ` +
        "their bytes — a zip's entry sizes are read from its central directory without " +
        "unpacking it, which is what makes an archive scan instantly, and a binary entry " +
        "therefore has a real size and no content. Pick the unpacked folder to compare those " +
        "files. They are counted in every total on this page.",
    );
  }
  if (pass.unheld > 0) {
    // A separate sentence for a separate cause. The zip sentence above told a
    // reader who had picked an unpacked folder to pick the unpacked folder.
    pass.notes.push(
      `${count(pass.unheld, "file")} could not be compared because this page holds no handle ` +
        `for ${pass.unheld === 1 ? "it" : "them"}: ${
          pass.unheld === 1 ? "it belongs" : "they belong"
        } to no resource in this drop, and only a folder pick hands over the files that sit ` +
        "outside every resource. Pick the folder itself with the folder button — dragging it in, " +
        `or scanning it as a zip, leaves ${pass.unheld === 1 ? "it" : "them"} unreadable here. ` +
        `${pass.unheld === 1 ? "It is" : "They are"} counted in every total on this page.`,
    );
  }
  if (pass.capped > 0) {
    pass.notes.push(
      `${count(pass.capped, "further file")} shared a size with another and ${
        pass.capped === 1 ? "was" : "were"
      } not read, because this pass stops at ${n(MAX_SIGNED_FILES)} files. A duplicate among ` +
        "them would not be reported here.",
    );
  }
  return pass;
}

// ─── One scan ────────────────────────────────────────────────────────────────

export type BudgetScan = {
  report: BudgetReport;
  /** What was scanned, for the saved slot's own label. */
  label: string;
  /** Every path the drop held, counted before any rule ran. */
  files: number;
  /** `null` when the reader turned the second pass off. */
  pass: SignaturePass | null;
  /**
   * The drop this was read from, kept so the second pass can be run afterwards
   * without picking the folder again.
   *
   * It holds `File` handles, not bytes — the browser is holding those anyway for
   * as long as the `<input>` keeps its `FileList`, and re-picking would build
   * the same handles back. What it buys is that "I ran it sizes-only and now I
   * want the duplicates" costs a click rather than a second trip through a
   * directory dialog, which is the difference between a switch a reader
   * experiments with and one they set once and never touch.
   */
  drop: ResourceDrop;
  /**
   * The gesture's own handles, kept for the same reason and with the same cost:
   * handles, not bytes. Without them a second comparison run over a folder
   * already in hand would lose the files that belong to no resource, which are
   * exactly the ones the first run was extended to reach.
   */
  held: ReadonlyMap<string, File>;
};

/** The top-level folder or archive names, so a saved slot can be told apart. */
function labelOf(drop: ResourceDrop): string {
  const tops = new Set<string>();
  for (const entry of drop.allPaths) {
    const top = entry.path.split("/")[0];
    if (top) tops.add(top);
    if (tops.size > 3) break;
  }
  const names = [...tops].sort();
  if (names.length === 0) return "an empty pick";
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} and more`;
}

/**
 * A `ResourceDrop`, read end to end.
 *
 * Exported so a test can drive the whole pipeline without a browser gesture, and
 * so the page has exactly one path from a drop to a scan whatever the gesture
 * was. The pass's own sentences are handed to `analyzeBudget` through the report
 * it returns rather than kept beside it, because the report's `skipped` list is
 * the one place this layer promises a reader will find everything that was not
 * done.
 */
export async function scanDrop(
  drop: ResourceDrop,
  options?: {
    compare?: boolean;
    signal?: AbortSignal;
    onProgress?: (done: number, total: number) => void;
    /** The gesture's own `File` handles; see `handlesOfPick`. */
    held?: ReadonlyMap<string, File>;
  },
): Promise<BudgetScan> {
  const compare = options?.compare !== false;
  const held = options?.held ?? new Map<string, File>();
  const pass = compare
    ? await readSignatures(drop, {
        signal: options?.signal,
        onProgress: options?.onProgress,
        held,
      })
    : null;

  const analyzed = analyzeBudget(drop, pass ? { signatures: pass.signatures } : undefined);
  // A new object rather than a mutated one: `analyzeBudget` is documented as
  // pure over its input and its output is what the JSON export writes verbatim.
  const report =
    pass && pass.notes.length > 0
      ? { ...analyzed, skipped: [...new Set([...analyzed.skipped, ...pass.notes])] }
      : analyzed;
  return { report, label: labelOf(drop), files: drop.allPaths.length, pass, drop, held };
}

// ─── Saving a scan, and diffing two ──────────────────────────────────────────

/** Underscored and versioned, as `workshop_resmon_captures_v1` and its neighbours are. */
export const STREAM_BUDGET_STORAGE_KEY = "workshop_stream_budget_scans_v1";

/**
 * How much a saved pair may come to.
 *
 * `localStorage` is a few megabytes per origin and shared with everything else
 * this site keeps there. A pair that will not fit is not written and the
 * interface says so, rather than silently failing to be there after a reload.
 */
const MAX_STORED_CHARS = 400_000;

/** Resource and extension rows a snapshot keeps. Enough to diff, small enough to store. */
const SNAPSHOT_ROWS = 400;

/**
 * What a saved scan remembers.
 *
 * Totals, buckets and one row per resource and per extension. Not the file rows:
 * a real `resources/` folder is tens of thousands of them and this store holds a
 * few megabytes for the whole origin. The page says what that costs — a diff can
 * see a resource grow and cannot see which file in it did — because a saved
 * artifact whose limits are not stated is the competitor's unqualified "Total
 * size" with our name on it.
 */
export type BudgetSnapshot = {
  label: string;
  savedAt: string;
  totalBytes: number;
  totalFiles: number;
  byBucket: Record<BudgetBucket, BudgetTally>;
  resources: { label: string; bytes: number; files: number }[];
  extensions: { extension: string; bytes: number; files: number }[];
  findings: number;
  /** True when more rows existed than the snapshot kept. */
  rowsTrimmed: boolean;
};

export function snapshotOf(scan: BudgetScan): BudgetSnapshot {
  const report = scan.report;
  return {
    label: scan.label,
    savedAt: new Date().toISOString(),
    totalBytes: report.totalBytes,
    totalFiles: report.totalFiles,
    byBucket: report.byBucket,
    resources: report.byResource.slice(0, SNAPSHOT_ROWS).map((row) => ({
      label: row.label,
      bytes: row.bytes,
      files: row.files,
    })),
    extensions: report.byExtension.slice(0, SNAPSHOT_ROWS).map((row) => ({ ...row })),
    findings: report.findings.length,
    rowsTrimmed:
      report.byResource.length > SNAPSHOT_ROWS || report.byExtension.length > SNAPSHOT_ROWS,
  };
}

export type BudgetDiffRow = {
  label: string;
  beforeBytes: number;
  afterBytes: number;
  deltaBytes: number;
  state: "added" | "removed" | "changed" | "same";
};

export type BudgetDiff = {
  deltaBytes: number;
  deltaFiles: number;
  buckets: { bucket: BudgetBucket; deltaBytes: number }[];
  resources: BudgetDiffRow[];
  /** What this comparison cannot see, in words. Never empty. */
  limits: string[];
};

/**
 * Two saved snapshots, compared.
 *
 * Rows are matched on the disambiguated label, which is what both the report and
 * the findings spell a resource as, so a row here and a row in either scan are
 * the same row. A resource renamed between two scans reads as one removed and
 * one added, which is the truthful answer: nothing in a folder listing says a
 * rename happened.
 */
export function diffSnapshots(before: BudgetSnapshot, after: BudgetSnapshot): BudgetDiff {
  const beforeRows = new Map(before.resources.map((row) => [row.label, row]));
  const afterRows = new Map(after.resources.map((row) => [row.label, row]));

  const rows: BudgetDiffRow[] = [];
  for (const label of new Set([...beforeRows.keys(), ...afterRows.keys()])) {
    const b = beforeRows.get(label);
    const a = afterRows.get(label);
    const beforeBytes = b?.bytes ?? 0;
    const afterBytes = a?.bytes ?? 0;
    rows.push({
      label,
      beforeBytes,
      afterBytes,
      deltaBytes: afterBytes - beforeBytes,
      state: !b ? "added" : !a ? "removed" : beforeBytes === afterBytes ? "same" : "changed",
    });
  }
  // Biggest movement first, then by label. `localeCompare` is deliberately not
  // used: it depends on the ICU data of the machine it runs on, so the same pair
  // of saved scans would order differently in two browsers and a diff nobody
  // made would appear between them. `streamBudget.ts` makes the same argument.
  rows.sort(
    (x, y) =>
      Math.abs(y.deltaBytes) - Math.abs(x.deltaBytes) ||
      (x.label < y.label ? -1 : x.label > y.label ? 1 : 0),
  );

  const limits = [
    "A saved scan keeps resource and extension totals, not a row per file, so this comparison " +
      "can tell you a resource grew and cannot tell you which file inside it did.",
    "A resource whose total did not move is reported as unchanged even if every file in it was " +
      "replaced with a file of the same size.",
  ];
  if (before.rowsTrimmed || after.rowsTrimmed) {
    limits.push(
      `One of these scans had more than ${n(SNAPSHOT_ROWS)} resource or extension rows, and only ` +
        "the largest were saved, so a small resource may be missing from this comparison.",
    );
  }

  return {
    deltaBytes: after.totalBytes - before.totalBytes,
    deltaFiles: after.totalFiles - before.totalFiles,
    buckets: BUDGET_BUCKETS.map((bucket) => ({
      bucket,
      deltaBytes: (after.byBucket[bucket]?.bytes ?? 0) - (before.byBucket[bucket]?.bytes ?? 0),
    })),
    resources: rows,
    limits,
  };
}

type Slots = { a: BudgetSnapshot | null; b: BudgetSnapshot | null };

function isSnapshot(value: unknown): value is BudgetSnapshot {
  const row = value as Partial<BudgetSnapshot> | null;
  return (
    !!row &&
    typeof row.label === "string" &&
    typeof row.totalBytes === "number" &&
    Array.isArray(row.resources) &&
    !!row.byBucket
  );
}

function readStoredSlots(): Slots {
  try {
    const raw = window.localStorage.getItem(STREAM_BUDGET_STORAGE_KEY);
    if (!raw) return { a: null, b: null };
    const parsed = JSON.parse(raw) as Partial<Slots>;
    return {
      a: isSnapshot(parsed?.a) ? parsed.a : null,
      b: isSnapshot(parsed?.b) ? parsed.b : null,
    };
  } catch {
    // A private window throws on the getter and a half-written value throws in
    // JSON.parse. Either way the right answer is an empty pair of slots.
    return { a: null, b: null };
  }
}

function writeStoredSlots(slots: Slots): boolean {
  try {
    const text = JSON.stringify(slots);
    if (text.length > MAX_STORED_CHARS) return false;
    window.localStorage.setItem(STREAM_BUDGET_STORAGE_KEY, text);
    return true;
  } catch {
    return false;
  }
}

// ─── Presentation vocabulary ─────────────────────────────────────────────────

/**
 * A colour per bucket.
 *
 * Six fixed tones rather than a gradient, because the bar and the legend have to
 * be matchable by eye at a glance and `other` in particular has to be
 * identifiable — an honestly large `other` is the whole argument for having one.
 */
const BUCKET_TONE: Record<BudgetBucket, { bar: string; dot: string }> = {
  stream: { bar: "bg-primary", dot: "bg-primary" },
  audio: { bar: "bg-violet-500", dot: "bg-violet-500" },
  script: { bar: "bg-emerald-500", dot: "bg-emerald-500" },
  data: { bar: "bg-amber-500", dot: "bg-amber-500" },
  media: { bar: "bg-sky-500", dot: "bg-sky-500" },
  other: { bar: "bg-muted-foreground/60", dot: "bg-muted-foreground/60" },
};

const TIER_TONE = {
  near: "border-warning/40 text-warning",
  warn: "border-destructive/40 text-destructive",
  high: "border-destructive/60 text-destructive",
} as const;

const TIER_WORD = {
  near: "near its ceiling",
  warn: "over its ceiling",
  high: "far over its ceiling",
} as const;

/**
 * What the waste tile is allowed to say.
 *
 * "0 bytes" is a verdict, and a verdict is exactly what a pass that opened
 * nothing did not earn. A zip is the case that makes this concrete: intake
 * never inflates a binary entry, so every candidate comes back unavailable and
 * the arithmetic is honestly zero over an archive that may hold 72 MB of
 * duplicated dictionaries. A partly-read pass is not zero either — it is a
 * floor — so it says so rather than rounding its own ignorance down.
 */
export function wastedFigure(scan: BudgetScan, wasted: number): string {
  const pass = scan.pass;
  if (pass === null) return "not compared";
  if (pass.candidates === 0) return formatBudgetBytes(0);
  if (pass.read === 0) return "not compared";
  if (pass.read < pass.candidates) return `at least ${formatBudgetBytes(wasted)}`;
  return formatBudgetBytes(wasted);
}

// ─── The page ────────────────────────────────────────────────────────────────

type Phase =
  | { kind: "idle" }
  | { kind: "reading"; stage: string; done: number; total: number }
  | { kind: "ready"; scan: BudgetScan }
  | { kind: "cancelled" }
  | { kind: "refused"; message: string }
  | { kind: "failed"; message: string };

export function StreamBudgetClient() {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [compare, setCompare] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [resourcesShown, setResourcesShown] = useState(BUDGET_RESOURCES_VISIBLE);
  const [assetsShown, setAssetsShown] = useState<Record<string, number>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [extensionsShown, setExtensionsShown] = useState(BUDGET_EXTENSIONS_VISIBLE);
  const [slots, setSlots] = useState<Slots>({ a: null, b: null });
  const [hydrated, setHydrated] = useState(false);
  const [storageNote, setStorageNote] = useState<string | null>(null);
  /**
   * Whether what is on screen came from a built-in sample pack.
   *
   * An argument of the read rather than a mode: every gesture goes through
   * `begin`, whose default is false, so real input clears it with nothing having
   * to remember to. While set, `DEMO_INPUT_NOTE` joins `report.skipped` — the one
   * list this layer promises holds everything that was not done — so it is on
   * screen and in all three exports at once.
   */
  const [demo, setDemo] = useState(false);
  /** Raised by the demo button only. See the mount at the bottom of the component. */
  const [tourSignal, setTourSignal] = useState(0);
  const abort = useRef<AbortController | null>(null);

  const scan = phase.kind === "ready" ? phase.scan : null;
  const report = useMemo(() => {
    const base = scan?.report ?? null;
    if (!base || !demo) return base;
    // A new object rather than a mutated one: `analyzeBudget` is documented as
    // pure over its input and its output is what the JSON export writes verbatim.
    return { ...base, skipped: [...new Set([...base.skipped, DEMO_INPUT_NOTE])] };
  }, [scan, demo]);

  // Read on the client only: seeding state from localStorage during render would
  // make the server and the first client render disagree.
  useEffect(() => {
    setSlots(readStoredSlots());
    setHydrated(true);
  }, []);

  const save = useCallback(
    (which: "a" | "b") => {
      if (!scan) return;
      setSlots((current) => {
        // A saved snapshot outlives the scan it came from — it survives a reload
        // and can be diffed against a real folder weeks later — so the demo fact
        // has to travel inside the snapshot itself, not in the page state that
        // will be gone by then. The label is the only field a reader sees on the
        // slot card and in the diff, so that is where it goes.
        const snapshot = snapshotOf(scan);
        const next = {
          ...current,
          [which]: demo ? { ...snapshot, label: `${DEMO_STATE_LABEL} — ${snapshot.label}` } : snapshot,
        };
        setStorageNote(
          writeStoredSlots(next)
            ? null
            : "This browser would not keep the saved scans — either they are too large for its " +
                "storage or this is a private window. They are on screen for as long as this tab " +
                "is open and will be gone after a reload.",
        );
        return next;
      });
    },
    [scan, demo],
  );

  const clearSlot = useCallback((which: "a" | "b") => {
    setSlots((current) => {
      const next = { ...current, [which]: null };
      writeStoredSlots(next);
      return next;
    });
  }, []);

  const begin = useCallback(
    async (
      read: (signal: AbortSignal) => Promise<ResourceDrop>,
      stage: string,
      comparePass: boolean,
      held?: ReadonlyMap<string, File>,
      fromDemo = false,
    ) => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      setPhase({ kind: "reading", stage, done: 0, total: 0 });
      setDemo(fromDemo);
      setResourcesShown(BUDGET_RESOURCES_VISIBLE);
      setAssetsShown({});
      setExpanded({});
      setExtensionsShown(BUDGET_EXTENSIONS_VISIBLE);
      try {
        const drop = await read(controller.signal);
        if (controller.signal.aborted) return;
        const next = await scanDrop(drop, {
          compare: comparePass,
          held,
          signal: controller.signal,
          onProgress: (done, total) => {
            if (controller.signal.aborted) return;
            setPhase({
              kind: "reading",
              stage: "Comparing the files that share a size",
              done,
              total,
            });
          },
        });
        if (controller.signal.aborted) return;
        setPhase({ kind: "ready", scan: next });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({
          kind: "failed",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setPhase({ kind: "cancelled" });
  }, []);

  /**
   * Run the comparison over the drop already in hand.
   *
   * Without this, a reader who scanned sizes-only and then wanted duplicates has
   * to go back through the directory dialog, and a switch that expensive to
   * change is one nobody changes. The drop is re-analysed from scratch rather
   * than patched, because a duplicate group and a high-detail pair interact —
   * the pair suppresses the generic duplicate finding over the same two files —
   * and half-updating a report is how a screen starts disagreeing with itself.
   */
  const compareNow = useCallback(() => {
    if (!scan) return;
    const kept = scan.drop;
    const handles = scan.held;
    setCompare(true);
    void begin(
      () => Promise.resolve(kept),
      "Comparing the files that share a size",
      true,
      handles,
      // The same drop re-analysed, so it is the same input it always was.
      // Dropping the flag here would take the sample-pack sentence off a report
      // still made of the sample pack.
      demo,
    );
  }, [scan, begin, demo]);

  // ── Gestures ──────────────────────────────────────────────────────────────

  const onFolder = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    const refusal = refusePick(files, "folder");
    if (refusal) {
      setPhase({ kind: "refused", message: refusal });
      return;
    }
    void begin(
      (signal) => fromDirectoryPick(list, { signal }),
      "Listing the folder",
      compare,
      // Every file the user picked, including the ones under no resource root.
      // Intake attributes what it can; this is what makes the rest comparable.
      handlesOfPick(files),
    );
  };

  const onArchives = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    const refusal = refusePick(files, "archive");
    if (refusal) {
      setPhase({ kind: "refused", message: refusal });
      return;
    }
    void begin((signal) => fromArchives(files, { signal }), "Reading the archive listing", compare);
  };

  const onDrop = (dt: DataTransferLike | null) => {
    setDragging(false);
    if (!dt) return;
    // The same refusal the two pickers run, before the read rather than after
    // it. Without it a dropped 4 GB zip is declined by intake, arrives as one
    // `allPaths` entry at its own size, and renders as a full report of an
    // archive nothing ever opened — a refusal turned into a number, which is
    // the failure mode the refusal phase exists for.
    const dropped = dt.files ? Array.from(dt.files) : [];
    const refusal = refusePick(dropped, "archive");
    if (refusal) {
      setPhase({ kind: "refused", message: refusal });
      return;
    }
    void begin(
      (signal) => fromDataTransfer(dt, { signal }),
      "Listing what was dropped",
      compare,
      // Top-level items only: `DataTransfer.files` does not contain the
      // contents of a dragged folder, which is why `fromDataTransfer` walks
      // `webkitGetAsEntry()` instead. A dropped loose file is still reachable.
      handlesOfPick(dropped),
    );
  };

  /**
   * A sample pack, through the gesture its registration names — which on this
   * tool is the folder pick, and not by preference.
   *
   * The duplicate pass signs the first bytes off a `File` handle. An archive
   * gesture has no handles to sign, because intake never inflated the binary
   * entry: the body is empty and the size is exact, so a zip demo here would
   * report its sizes correctly and say, truthfully, that duplicate detection did
   * not run — a demo of the duplicate finding with no duplicate in it. So the
   * pick's own files are handed to `handlesOfPick`, exactly as `onFolder` does,
   * and the compare pass is forced on: the switch is the user's everywhere
   * except here, where the demo would otherwise teach nothing.
   */
  const demoPacks = useMemo(() => demoPacksFor("stream-budget"), []);
  const loadDemo = useCallback(
    async (pack: DemoPack, use: DemoPackUse) => {
      if (use.gesture === "pick") {
        const list = pack.pick();
        const files = Array.from(list);
        setCompare(true);
        await begin(
          (signal) => fromDirectoryPick(list, { signal }),
          "Listing the sample pack",
          true,
          handlesOfPick(files),
          true,
        );
      } else {
        const files = await pack.archives();
        await begin(
          (signal) => fromArchives(files, { signal }),
          "Reading the sample archives",
          compare,
          undefined,
          true,
        );
      }
      setTourSignal((signal) => signal + 1);
    },
    [begin, compare],
  );

  /** Prefixed while a demo is loaded; see `demoDownloadName`. */
  const exportName = useCallback((name: string) => (demo ? demoDownloadName(name) : name), [demo]);

  // ── Derived ───────────────────────────────────────────────────────────────

  /**
   * What deleting the redundant copies would actually return.
   *
   * Over the union of the two lists, never their sum. The two overlap by
   * construction whenever a duplicate group holds a high-detail pair *and* a
   * third copy — `pack-a/car.yft`, `pack-a/car_hi.yft` and `pack-b/car.yft`,
   * all the same bytes, is one duplicate group worth two copies and one
   * identical pair worth one, and adding them claims the whole drop back. A
   * group's `wastedBytes` is already `bytes × (copies − 1)`, which counts
   * deleting the companion, so a pair whose two paths are both inside a group
   * has been paid for.
   */
  const wasted = useMemo(() => {
    if (!report) return 0;
    const charged = new Set<string>();
    let total = 0;
    for (const group of report.duplicates) {
      total += group.wastedBytes;
      for (const copy of group.copies) charged.add(pathKey(copy.path));
    }
    for (const pair of report.identicalLodPairs) {
      if (charged.has(pathKey(pair.basePath)) && charged.has(pathKey(pair.companionPath))) continue;
      total += pair.bytes;
      charged.add(pathKey(pair.basePath));
      charged.add(pathKey(pair.companionPath));
    }
    return total;
  }, [report]);

  const diff = useMemo(
    () => (slots.a && slots.b ? diffSnapshots(slots.a, slots.b) : null),
    [slots],
  );

  return (
    <div className="space-y-8">
      {/* ── Intake ──────────────────────────────────────────────────────── */}
      <section
        data-testid="stream-budget-drop"
        data-walkthrough="stb-drop"
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          onDrop(event.dataTransfer as unknown as DataTransferLike);
        }}
        className={`not-prose rounded-2xl border-2 border-dashed p-5 transition-colors sm:p-6 ${
          dragging ? "border-primary/60 bg-primary/5" : "border-border/70 bg-muted/30"
        }`}
      >
        <h2 className="text-base font-semibold text-foreground">
          Drop a resource, a <code className="font-mono">resources/</code> folder, or a zip
        </h2>
        {/* Two claims and a scope line, and nothing else.
            What came out: "so a 39 MB archive is listed in about the time it
            takes to open it" and "a folder pick never loads a file into memory
            at all" — the tool arguing its own performance — and "there is no
            request in this page and no server behind it", which is reassurance
            after the claim it reassures about has already been made. The
            identical clause shapes were cut from `SpawnIndexClient`'s intake
            and are now standing negative guards in its suite, so the two
            islands had opposite rules for the same sentence.
            What stays, in words: every file is counted. That is a category-A
            statement about scope — a reader who assumes this counts only
            streamed assets would read every figure on the page wrong — so it
            is not a candidate for the cut at any budget. The long form of the
            zip mechanism survives in the page's article and its FAQ. */}
        <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground">
          The first pass reads <strong className="text-foreground">sizes only</strong> — a zip&apos;s
          entry sizes come from its central directory, unopened. Every file is counted — scripts,{" "}
          <code className="font-mono">.meta</code>, <code className="font-mono">.dat</code>,{" "}
          <code className="font-mono">.rel</code>, loose images and all.{" "}
          <strong className="text-foreground">Nothing is uploaded</strong>.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
            <FolderOpen className="h-4 w-4" />
            Pick a folder
            <input
              data-testid="stream-budget-folder"
              type="file"
              multiple
              className="sr-only"
              onChange={(event) => onFolder(event.target.files)}
              {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40">
            <Upload className="h-4 w-4" />
            Pick zips
            <input
              data-testid="stream-budget-archives"
              type="file"
              multiple
              accept=".zip"
              className="sr-only"
              onChange={(event) => onArchives(event.target.files)}
            />
          </label>
          {/* In the intake row and gated on nothing — see `ShowTourButton`. The
              sample-pack panel below is gone the moment a report exists, so it
              cannot be the only route to the tour, and the reader who dropped
              their own folder is the one who needed it most. */}
          <ShowTourButton
            testId="stream-budget-show-tour"
            onClick={() => setTourSignal((signal) => signal + 1)}
          />
        </div>

        {/* The second pass, named and switchable. A duplicate verdict this page
            did not earn is the one thing it may not print.

            The label is the name of the thing plus the one mechanical fact a
            reader decides on — which files get opened. What the pass *finds*
            (six packs shipping one dictionary, a `_hi` model byte-identical to
            its own base) is in the page's article and in the findings the pass
            produces, where it is a result rather than a promise.

            What is deliberately not here any more: "the report says the
            comparison did not happen rather than saying there are no
            duplicates". That was this control describing a sentence somewhere
            else, and the sentence exists — `:1155` below prints "The second
            pass was switched off", `analyzeBudget` puts "Duplicate detection
            did not run" in `report.skipped`, and every export carries it. The
            cost of the cut is real and is recorded rather than papered over:
            the idle state, before any folder exists, no longer says it. */}
        <label
          data-walkthrough="stb-compare"
          className="mt-4 flex max-w-2xl cursor-pointer items-start gap-2.5 text-sm text-muted-foreground"
        >
          <input
            data-testid="stream-budget-compare"
            type="checkbox"
            checked={compare}
            onChange={(event) => setCompare(event.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
          />
          <span className="leading-7">
            <strong className="text-foreground">Second pass: find duplicates.</strong> Reads the
            first {n(SIGNATURE_PREFIX_BYTES)} bytes of same-size files only.
          </span>
        </label>

        {/* ── The worked example, in the empty state only ──
            Labelled with the finding each pack produces, and gone the moment a
            scan exists — which is honesty rule 3 stated as the absence of a
            control rather than as a dialog nobody reads. */}
        {phase.kind !== "ready" && demoPacks.length > 0 && (
          <div className="mt-4 rounded-xl border border-border/60 bg-background/60 p-4">
            <p className="text-xs font-medium text-foreground">
              No folder to hand? Load a sample pack and see what a duplicate costs.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {demoPacks.map(({ pack, use }) => (
                <button
                  key={pack.id}
                  type="button"
                  data-testid="stream-budget-demo"
                  disabled={phase.kind === "reading"}
                  onClick={() => void loadDemo(pack, use)}
                  className="max-w-full rounded-lg border border-border bg-card px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:border-primary/40 disabled:opacity-50"
                >
                  <span className="block text-foreground">{pack.label}</span>
                  <span className="mt-0.5 block font-normal leading-5 text-muted-foreground">
                    {use.teaches}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Beside the drop zone's own state, never behind a disclosure. */}
        {scan && demo && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span
              data-testid="stream-budget-demo-state"
              className="inline-flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-1.5 text-xs font-medium text-foreground"
            >
              <AlertTriangle className="h-3.5 w-3.5 text-warning" />
              {DEMO_STATE_LABEL}
            </span>
          </div>
        )}

        {phase.kind === "reading" && (
          <div
            data-testid="stream-budget-progress"
            className="mt-4 flex flex-wrap items-center gap-3"
          >
            <p className="text-sm text-muted-foreground">
              {phase.stage}
              {phase.total > 0 ? ` — ${n(phase.done)} of ${n(phase.total)} files` : "…"}
            </p>
            <button
              type="button"
              data-testid="stream-budget-cancel"
              onClick={cancel}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </div>
        )}
        {phase.kind === "cancelled" && (
          <p className="mt-4 text-sm text-muted-foreground">
            Cancelled. Nothing was scanned and nothing was kept.
          </p>
        )}
        {phase.kind === "refused" && (
          <p
            data-testid="stream-budget-refused"
            className="mt-4 flex items-start gap-2 text-sm text-destructive"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{phase.message}</span>
          </p>
        )}
        {phase.kind === "failed" && (
          <p className="mt-4 flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>That folder could not be read: {phase.message}. Nothing was scanned.</span>
          </p>
        )}
        {scan && scan.pass === null && (
          <button
            type="button"
            data-testid="stream-budget-compare-now"
            onClick={compareNow}
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
          >
            <ChevronRight className="h-3.5 w-3.5" />
            Run the comparison on this folder
          </button>
        )}
        {scan && (
          <p className="mt-4 text-xs leading-6 text-muted-foreground">
            {count(scan.files, "file")} seen.{" "}
            {scan.pass === null
              ? "The second pass was switched off, so no file was opened and no two files were compared."
              : scan.pass.candidates === 0
                ? "No two files in this drop share a size, so there was nothing for the second pass to compare and none was opened."
                : `${count(scan.pass.candidates, "file")} shared a size with another and ` +
                  `${n(scan.pass.read)} of them had their first ${n(SIGNATURE_PREFIX_BYTES)} bytes read. ` +
                  "Nothing else was opened."}
          </p>
        )}
      </section>

      {report && scan && (
        <>
          {/* ── The figures. Every one counts every row. ────────────────── */}
          {/*
            One section, so the caption belongs to the grid rather than floating
            after it. What qualifies the headline number is now in two places
            and both of them are attached to it: a six-word note inside the tile,
            and this caption directly under the four.

            It was one 119-word paragraph, the largest in this island, and it
            failed the rule it exists to serve. Under a tile grid, in the same
            grey as the domain explanation above, at a length a reader skips —
            so the claim that most needs to land was in the register that
            guarantees it will not. None of it went behind a disclosure: a
            qualification of the biggest number on the page behind a closed
            triangle has not been told.

            What was deleted is the half with no user in it — "a pack whose
            manifest is wrong is the pack most likely to be scanned" (this tool
            arguing its own case), the pointer to the resmon tool (cross-sell),
            and "size on disk: every file in the drop, in exactly one bucket",
            which the bucket legend fifty lines below already says.
          */}
          <section aria-label="Totals" data-walkthrough="stb-totals" className="space-y-2">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {/* Labelled what it is. "Downloaded on first join" is the one claim
                  the analysis cannot support: a server_script is never sent to a
                  client and a file no manifest names is downloaded by nobody, and
                  both are counted here. The note and the caption carry that. */}
              <Figure
                label="Every byte in this drop"
                value={formatBudgetBytes(report.totalBytes)}
                note="disk size, not a download"
                testId="stream-budget-total"
              />
              <Figure label="Files" value={n(report.totalFiles)} />
              {/* The row for files under no resource root is a row, not a
                  resource — which is why the library skips every per-resource
                  rule on it. The table's own caption below says "rows". */}
              <Figure
                label="Resources"
                value={n(report.byResource.filter((row) => row.kind === "resource").length)}
                testId="stream-budget-resources"
              />
              <Figure
                label="Wasted on duplicates"
                value={wastedFigure(scan, wasted)}
                tone={wasted > 0 ? "bad" : "plain"}
                testId="stream-budget-wasted"
              />
            </div>

            <p className="text-xs leading-6 text-muted-foreground">
              A <code className="font-mono">server_script</code> is never sent to a client, and a
              file no manifest names is downloaded by nobody. Both are counted here. It is not VRAM
              and not server load.
            </p>
          </section>

          {/* ── Where the bytes went ───────────────────────────────────── */}
          <section
            aria-label="Where the bytes went"
            data-walkthrough="stb-buckets"
            className="space-y-3"
          >
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Where the bytes went
            </h2>
            <div className="flex h-4 w-full overflow-hidden rounded-full bg-muted">
              {BUDGET_BUCKETS.map((bucket) => {
                const tally = report.byBucket[bucket];
                if (tally.bytes <= 0 || report.totalBytes <= 0) return null;
                return (
                  <div
                    key={bucket}
                    // True proportion, no floor. A bucket too thin to see is
                    // named in the legend with its exact byte count instead.
                    style={{ width: `${(tally.bytes / report.totalBytes) * 100}%` }}
                    className={BUCKET_TONE[bucket].bar}
                    title={`${BUDGET_BUCKET_LABELS[bucket]} — ${formatBudgetBytes(tally.bytes)}`}
                  />
                );
              })}
            </div>
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {BUDGET_BUCKETS.map((bucket) => {
                const tally = report.byBucket[bucket];
                return (
                  <div
                    key={bucket}
                    data-testid="stream-budget-bucket"
                    className="flex items-baseline gap-2 text-xs"
                  >
                    <span
                      className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-sm ${BUCKET_TONE[bucket].dot}`}
                    />
                    <dt className="text-foreground">{BUDGET_BUCKET_LABELS[bucket]}</dt>
                    <dd className="ml-auto text-right text-muted-foreground">
                      {share(tally.bytes, report.totalBytes)} ·{" "}
                      {formatBudgetBytes(tally.bytes)} · {count(tally.files, "file")}
                    </dd>
                  </div>
                );
              })}
            </dl>
            <p className="text-xs leading-6 text-muted-foreground">
              Every file in the drop is in exactly one of these six, and the six add up to the
              figure above. A large <em>{BUDGET_BUCKET_LABELS.other.toLowerCase()}</em> is not an
              error — it is source art, backups and archives left inside a resource, downloaded by
              every client and read by nothing.
            </p>
          </section>

          {/* ── Exports ─────────────────────────────────────────────────── */}
          <section aria-label="Exports" className="flex flex-wrap gap-2">
            <ExportButton
              icon={FileSpreadsheet}
              label="Spreadsheet (.csv)"
              onClick={() =>
                downloadTextFile(
                  exportName("stream-budget.csv"),
                  budgetToCsv(report),
                  "text/csv",
                )
              }
            />
            <ExportButton
              icon={FileText}
              label="Paste (.md)"
              onClick={() =>
                downloadTextFile(
                  exportName("stream-budget.md"),
                  budgetToMarkdown(report),
                  "text/markdown",
                )
              }
            />
            <ExportButton
              icon={FileJson}
              label="Report (.json)"
              onClick={() =>
                downloadTextFile(
                  exportName("stream-budget.json"),
                  JSON.stringify(report, null, 2),
                  "application/json",
                )
              }
            />
          </section>

          {/* ── Findings, through the shared surface ────────────────────── */}
          <section aria-label="Problems" data-walkthrough="stb-findings">
            <FindingsList
              findings={report.findings}
              truncated={report.skipped}
              emptyLabel="Nothing in this drop is over a budget in the checks that ran."
            />
          </section>

          {/* ── By resource ─────────────────────────────────────────────── */}
          <section aria-label="By resource" className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 className="text-xl font-semibold tracking-tight text-foreground">By resource</h2>
              <p className="text-xs text-muted-foreground" data-testid="stream-budget-scope">
                {count(report.byResource.length, "row")}, largest first, totalling{" "}
                {formatBudgetBytes(report.totalBytes)}.
              </p>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-border/60 bg-card">
              <table className="w-full min-w-[46rem] text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">Resource</th>
                    <th className="px-4 py-2.5 font-medium">Size</th>
                    <th className="px-4 py-2.5 font-medium">Share</th>
                    <th className="px-4 py-2.5 font-medium">Files</th>
                    <th className="px-4 py-2.5 font-medium">Biggest bucket</th>
                  </tr>
                </thead>
                <tbody>
                  {report.byResource.slice(0, resourcesShown).map((row) => (
                    <ResourceRows
                      key={row.identity || row.label}
                      row={row}
                      totalBytes={report.totalBytes}
                      open={expanded[row.identity || row.label] ?? false}
                      shown={assetsShown[row.identity || row.label] ?? BUDGET_ASSETS_VISIBLE}
                      onToggle={() =>
                        setExpanded((current) => ({
                          ...current,
                          [row.identity || row.label]: !(current[row.identity || row.label] ?? false),
                        }))
                      }
                      onMore={() =>
                        setAssetsShown((current) => ({
                          ...current,
                          [row.identity || row.label]:
                            (current[row.identity || row.label] ?? BUDGET_ASSETS_VISIBLE) +
                            BUDGET_ASSETS_PAGE,
                        }))
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {report.byResource.length > resourcesShown && (
              <button
                type="button"
                onClick={() => setResourcesShown((current) => current + BUDGET_RESOURCES_PAGE)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30"
              >
                <ChevronRight className="h-4 w-4" />
                Show{" "}
                {n(Math.min(report.byResource.length - resourcesShown, BUDGET_RESOURCES_PAGE))} more
                of {count(report.byResource.length, "row")}
              </button>
            )}
          </section>

          {/* ── By extension ────────────────────────────────────────────── */}
          <section aria-label="By extension" className="space-y-3">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">By extension</h2>
            <div className="overflow-x-auto rounded-2xl border border-border/60 bg-card">
              <table className="w-full min-w-[36rem] text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">Extension</th>
                    <th className="px-4 py-2.5 font-medium">Size</th>
                    <th className="px-4 py-2.5 font-medium">Share</th>
                    <th className="px-4 py-2.5 font-medium">Files</th>
                  </tr>
                </thead>
                <tbody>
                  {report.byExtension.slice(0, extensionsShown).map((row) => (
                    <tr
                      key={row.extension}
                      data-testid="stream-budget-extension"
                      className="border-b border-border/40 last:border-0"
                    >
                      <td className="px-4 py-2 font-mono text-xs text-foreground">
                        {row.extension === "" ? "no extension" : `.${row.extension}`}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {formatBudgetBytes(row.bytes)}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {share(row.bytes, report.totalBytes)}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{n(row.files)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {report.byExtension.length > extensionsShown && (
              <button
                type="button"
                onClick={() => setExtensionsShown((current) => current + BUDGET_EXTENSIONS_PAGE)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30"
              >
                <ChevronRight className="h-4 w-4" />
                Show{" "}
                {n(Math.min(report.byExtension.length - extensionsShown, BUDGET_EXTENSIONS_PAGE))}{" "}
                more of {count(report.byExtension.length, "extension")}
              </button>
            )}
          </section>

          {/* ── The largest files ───────────────────────────────────────── */}
          <section aria-label="Largest files" className="space-y-3">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              The {n(report.largest.length)} largest files
            </h2>
            <ul className="divide-y divide-border/50 rounded-2xl border border-border/60 bg-card">
              {report.largest.map((row) => (
                <li
                  key={row.path}
                  data-testid="stream-budget-largest"
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5"
                >
                  <code className="font-mono text-xs text-foreground">{row.path}</code>
                  {row.tier && <Chip tone={row.tier}>{TIER_WORD[row.tier]}</Chip>}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatBudgetBytes(row.bytes)}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* ── Save and compare ────────────────────────────────────────── */}
          <section aria-label="Compare two scans" className="space-y-3">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Before and after
            </h2>
            <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
              Save this scan, change the pack, scan it again and save it to the other slot. The two
              stay in this browser — nothing is sent anywhere — and a saved scan keeps the totals
              per resource and per extension, not a row per file, which is what keeps a whole
              server folder inside a store measured in megabytes.
            </p>
            <div className="flex flex-wrap gap-2">
              <SlotCard
                name="Before"
                snapshot={slots.a}
                onSave={() => save("a")}
                onClear={() => clearSlot("a")}
                disabled={!hydrated}
              />
              <SlotCard
                name="After"
                snapshot={slots.b}
                onSave={() => save("b")}
                onClear={() => clearSlot("b")}
                disabled={!hydrated}
              />
            </div>
            {storageNote && <p className="text-xs leading-6 text-warning">{storageNote}</p>}
            {diff && <DiffPanel diff={diff} />}
          </section>
        </>
      )}

      {/* Manual mode: the demo button and the replay button raise the signal,
          and a report appearing never does. A tour that opened because a result
          appeared would open over a stranger's own 40 GB folder, with the demo
          buttons that would have explained it already gone. */}
      <ToolWalkthrough
        /* The sample-announcing first step is dropped when no sample is
           loaded, and this is an honesty fix rather than a tidy-up. That step
           says "not your files" — true over a demo, and a straightforward
           falsehood over a folder the reader picked themselves, which the
           permanent "Show me how this works" control now makes reachable.
           Filtered by `id` and not by index so reordering the array cannot
           silently drop a different step. Nothing is lost on the demo path,
           and nothing is claimed on the real one. */
        steps={demo ? streamBudgetSteps : streamBudgetSteps.filter((step) => step.id !== "welcome")}
        storageKey="workshop_tour_stream_budget_v1"
        finishLabel={streamBudgetFinishLabel}
        openSignal={tourSignal}
      />
    </div>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

/**
 * One figure, and the caveat that belongs to that figure rather than to the page.
 *
 * `note` is the generalisation of `wastedFigure`. That function already puts an
 * honesty claim *inside* a number — "not compared" and "at least 4.2 MB" in
 * place of a figure the pass did not earn — and it works because anyone who
 * reads the number reads it. This is the same move for a number that is exact
 * but means less than its label suggests: the total is every byte on disk and a
 * reader arrives expecting a download size.
 *
 * Six words, because a caption that wraps to three lines is the paragraph this
 * prop exists to replace. Not a tooltip either: an honesty claim behind a hover
 * does not exist on a touch device, and the reader most likely to need it is
 * the one on a phone.
 */
function Figure({
  label,
  value,
  tone = "plain",
  note,
  testId,
}: {
  label: string;
  value: string;
  tone?: "plain" | "bad";
  note?: string;
  testId?: string;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card px-4 py-3" data-testid={testId}>
      <div
        className={`font-mono text-sm font-semibold leading-6 ${
          tone === "bad" ? "text-destructive" : "text-foreground"
        }`}
      >
        {value}
      </div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {note !== undefined && <div className="mt-0.5 text-[11px] text-muted-foreground">{note}</div>}
    </div>
  );
}

function Chip({ children, tone }: { children: ReactNode; tone: keyof typeof TIER_TONE }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] leading-5 ${TIER_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

function ExportButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Download;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40"
    >
      <Icon className="h-4 w-4 text-primary/80" />
      {label}
    </button>
  );
}

/**
 * One resource, and — when it is open — a bounded slice of its files.
 *
 * Two `<tr>`s rather than a nested table, so the file rows line up under the
 * columns they belong to and the whole thing stays one horizontally scrollable
 * region at phone width.
 */
function ResourceRows({
  row,
  totalBytes,
  open,
  shown,
  onToggle,
  onMore,
}: {
  row: BudgetResourceRow;
  totalBytes: number;
  open: boolean;
  shown: number;
  onToggle: () => void;
  onMore: () => void;
}) {
  const biggest = BUDGET_BUCKETS.map((bucket) => ({ bucket, ...row.buckets[bucket] })).sort(
    (a, b) => b.bytes - a.bytes,
  )[0];
  const remaining = row.assets.length - shown;

  return (
    <>
      <tr data-testid="stream-budget-resource" className="border-b border-border/40">
        <td className="px-4 py-2">
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex items-center gap-1.5 text-left font-mono text-xs text-foreground transition-colors hover:text-primary"
          >
            <ChevronRight
              className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${
                open ? "rotate-90" : ""
              }`}
            />
            {row.label}
          </button>
          {row.kind === "unassigned" && (
            <span className="mt-0.5 block text-[11px] leading-5 text-muted-foreground">
              Not inside any resource. Counted, named, and not folded into a neighbour.
            </span>
          )}
        </td>
        <td className="px-4 py-2 text-xs text-muted-foreground">{formatBudgetBytes(row.bytes)}</td>
        <td className="px-4 py-2 text-xs text-muted-foreground">{share(row.bytes, totalBytes)}</td>
        <td className="px-4 py-2 text-xs text-muted-foreground">{n(row.files)}</td>
        <td className="px-4 py-2 text-xs text-muted-foreground">
          {biggest && biggest.bytes > 0
            ? `${BUDGET_BUCKET_LABELS[biggest.bucket]} · ${formatBudgetBytes(biggest.bytes)}`
            : "—"}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border/40 bg-muted/20">
          <td colSpan={5} className="px-4 py-3">
            <ul className="space-y-1">
              {row.assets.slice(0, shown).map((asset) => (
                <li
                  key={asset.path}
                  data-testid="stream-budget-asset"
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                >
                  <code className="font-mono text-foreground">{asset.path}</code>
                  {asset.tier && <Chip tone={asset.tier}>{TIER_WORD[asset.tier]}</Chip>}
                  <span className="ml-auto text-muted-foreground">
                    {formatBudgetBytes(asset.bytes)}
                  </span>
                </li>
              ))}
            </ul>
            {remaining > 0 && (
              <button
                type="button"
                onClick={onMore}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
              >
                <ChevronRight className="h-3.5 w-3.5" />
                Show {n(Math.min(remaining, BUDGET_ASSETS_PAGE))} more of {n(row.assets.length)}
              </button>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function SlotCard({
  name,
  snapshot,
  onSave,
  onClear,
  disabled,
}: {
  name: string;
  snapshot: BudgetSnapshot | null;
  onSave: () => void;
  onClear: () => void;
  disabled: boolean;
}) {
  return (
    <div className="min-w-[16rem] flex-1 rounded-xl border border-border/60 bg-card p-4">
      <h3 className="text-sm font-semibold text-foreground">{name}</h3>
      {snapshot ? (
        <>
          <p className="mt-1 text-xs leading-6 text-muted-foreground">
            <span className="font-mono text-foreground">{snapshot.label}</span> —{" "}
            {formatBudgetBytes(snapshot.totalBytes)} across {count(snapshot.totalFiles, "file")}.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onSave}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/30 disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" />
              Replace with this scan
            </button>
            <button
              type="button"
              onClick={onClear}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-1 text-xs leading-6 text-muted-foreground">Nothing saved here yet.</p>
          <button
            type="button"
            onClick={onSave}
            disabled={disabled}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/30 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            Save this scan as {name}
          </button>
        </>
      )}
    </div>
  );
}

/** How many diff rows are drawn. The counts above them count every row. */
const DIFF_ROWS_VISIBLE = 20;

function DiffPanel({ diff }: { diff: BudgetDiff }) {
  const [shown, setShown] = useState(DIFF_ROWS_VISIBLE);
  const moved = diff.resources.filter((row) => row.state !== "same");

  return (
    <div data-testid="stream-budget-diff" className="rounded-2xl border border-border/60 bg-card p-4">
      {/* After minus before, which is what every number under it is. A heading
          that states the arithmetic backwards makes a reader read the sign of
          their own afternoon's work the wrong way round. */}
      <h3 className="text-sm font-semibold text-foreground">After minus before</h3>
      <p className="mt-1 text-sm text-foreground">
        {diff.deltaBytes === 0
          ? "The two scans are the same size."
          : `${diff.deltaBytes > 0 ? "Up" : "Down"} ${formatBudgetBytes(Math.abs(diff.deltaBytes))}`}
        {diff.deltaFiles !== 0 &&
          ` · ${diff.deltaFiles > 0 ? "+" : "−"}${count(Math.abs(diff.deltaFiles), "file")}`}
      </p>

      <dl className="mt-3 grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
        {diff.buckets
          .filter((entry) => entry.deltaBytes !== 0)
          .map((entry) => (
            <div key={entry.bucket} className="flex items-baseline gap-2 text-xs">
              <dt className="text-foreground">{BUDGET_BUCKET_LABELS[entry.bucket]}</dt>
              <dd className="ml-auto text-muted-foreground">
                {entry.deltaBytes > 0 ? "+" : "−"}
                {formatBudgetBytes(Math.abs(entry.deltaBytes))}
              </dd>
            </div>
          ))}
      </dl>

      {moved.length > 0 ? (
        <>
          <ul className="mt-3 divide-y divide-border/40">
            {moved.slice(0, shown).map((row) => (
              <li
                key={row.label}
                data-testid="stream-budget-diff-row"
                className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-xs"
              >
                <code className="font-mono text-foreground">{row.label}</code>
                <span className="text-muted-foreground">{row.state}</span>
                <span className="ml-auto text-muted-foreground">
                  {row.deltaBytes > 0 ? "+" : "−"}
                  {formatBudgetBytes(Math.abs(row.deltaBytes))}
                </span>
              </li>
            ))}
          </ul>
          {moved.length > shown && (
            <button
              type="button"
              onClick={() => setShown((current) => current + DIFF_ROWS_VISIBLE)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
            >
              <ChevronRight className="h-3.5 w-3.5" />
              Show {n(Math.min(moved.length - shown, DIFF_ROWS_VISIBLE))} more of{" "}
              {count(moved.length, "changed row")}
            </button>
          )}
        </>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">
          No resource changed size between the two saved scans.
        </p>
      )}

      <ul className="mt-3 space-y-1">
        {diff.limits.map((sentence) => (
          <li key={sentence} className="flex gap-2 text-[11px] leading-5 text-muted-foreground">
            <AlertTriangle className="mt-1 h-3 w-3 shrink-0 text-warning" />
            <span>{sentence}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
